import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import type { ChatMessage, CreationStatus, ProposedPanel } from '@/types/Dashboards.types'

interface ChatSessionState {
  sessionId: string | null
  creationStatus: CreationStatus | null
  messages: ChatMessage[]
  proposedPanels: ProposedPanel[]
  selectedPanelIndices: number[]
  isLoading: boolean
  isSending: boolean
  error: string | null
}

const initialState: ChatSessionState = {
  sessionId: null,
  creationStatus: null,
  messages: [],
  proposedPanels: [],
  selectedPanelIndices: [],
  isLoading: false,
  isSending: false,
  error: null,
}

const chatSessionSlice = createSlice({
  name: 'chatSession',
  initialState,
  reducers: {
    setSession(
      state,
      action: PayloadAction<{
        sessionId: string
        creationStatus: CreationStatus
        messages: ChatMessage[]
      }>
    ) {
      state.sessionId = action.payload.sessionId
      state.creationStatus = action.payload.creationStatus
      state.messages = action.payload.messages
    },

    appendMessage(state, action: PayloadAction<ChatMessage>) {
      state.messages = [...state.messages, action.payload]
    },

    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload
    },

    setIsSending(state, action: PayloadAction<boolean>) {
      state.isSending = action.payload
    },

    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },

    setCreationStatus(state, action: PayloadAction<CreationStatus>) {
      state.creationStatus = action.payload
    },

    setProposedPanels(state, action: PayloadAction<ProposedPanel[]>) {
      if (!Array.isArray(action.payload)) return
      state.proposedPanels = action.payload
      state.selectedPanelIndices = []
    },

    setSelectedPanelIndices(state, action: PayloadAction<number[]>) {
      state.selectedPanelIndices = action.payload
    },

    toggleProposedPanel(state, action: PayloadAction<number>) {
      const index = action.payload
      const existing = state.selectedPanelIndices.indexOf(index)
      if (existing >= 0) {
        state.selectedPanelIndices = state.selectedPanelIndices.filter(i => i !== index)
      } else {
        state.selectedPanelIndices = [...state.selectedPanelIndices, index].sort((a, b) => a - b)
      }
    },

    clearSession() {
      return initialState
    },
  },
})

export const chatSessionReducer = chatSessionSlice.reducer
export const chatSessionActions = chatSessionSlice.actions
export default chatSessionSlice
